import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import { Colors, Radius, Spacing, Typography } from '../theme';
import { findSupportedCity } from '../utils/cities';
import { useI18n } from '../i18n';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/themes';

export interface PlaceSuggestion {
  placeId: string;
  displayName: string;
  venueName: string;
  address: string;
  city: string;
  lat: number;
  lng: number;
}

interface Props {
  value: PlaceSuggestion | null;
  cityKey: string;
  onChange: (place: PlaceSuggestion | null) => void;
  onTextChange?: (text: string) => void;
}

const DEBOUNCE_MS = 380;
const MAX_SUGGESTIONS = 5;

function parseNominatimResult(result: Record<string, any>): PlaceSuggestion {
  const addr = result.address ?? {};
  const venueName =
    result.name ||
    addr.amenity ||
    addr.building ||
    addr.shop ||
    addr.leisure ||
    (String(result.display_name ?? '')).split(', ')[0] ||
    '';
  const city =
    addr.city || addr.town || addr.village || addr.municipality || addr.county || '';
  const road = [addr.house_number, addr.road].filter(Boolean).join(' ');
  const address = [road, addr.suburb, addr.postcode, city, addr.country]
    .filter(Boolean)
    .join(', ');
  return {
    placeId: String(result.place_id ?? result.osm_id ?? Math.random()),
    displayName: result.display_name ?? '',
    venueName,
    address: address || result.display_name || '',
    city,
    lat: parseFloat(result.lat),
    lng: parseFloat(result.lon),
  };
}

const LocationAutocomplete: React.FC<Props> = ({ value, cityKey, onChange, onTextChange }) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  const [query, setQuery] = useState(value?.displayName ?? '');
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (value) {
      setQuery(value.displayName);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [value]);

  const fetchSuggestions = useCallback(
    async (text: string, currentCityKey: string) => {
      if (text.trim().length < 3) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      abortRef.current?.abort();
      abortRef.current = new AbortController();

      const cityLabel = findSupportedCity(currentCityKey)?.label ?? '';
      const q = cityLabel ? `${text.trim()}, ${cityLabel}` : text.trim();
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=${MAX_SUGGESTIONS}&addressdetails=1&accept-language=en`;

      if (__DEV__) console.log('[Tonyte][UploadLocation] input', { text: text.trim(), cityKey: currentCityKey });
      setLoadingSuggestions(true);
      try {
        const res = await fetch(url, {
          signal: abortRef.current.signal,
          headers: { 'User-Agent': 'TonyteApp/1.0' },
        });
        const json: Record<string, any>[] = await res.json();
        const parsed = json
          .map(parseNominatimResult)
          .filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng) && p.lat !== 0);
        if (__DEV__) {
          console.log('[Tonyte][UploadLocation] suggestions', {
            count: parsed.length,
            results: parsed.map(p => p.venueName || p.address),
          });
        }
        setSuggestions(parsed);
        setShowSuggestions(parsed.length > 0);
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          console.warn('[Tonyte][UploadLocation] fetch failed', err);
          setSuggestions([]);
          setShowSuggestions(false);
        }
      } finally {
        setLoadingSuggestions(false);
      }
    },
    [],
  );

  const handleTextChange = (text: string) => {
    setQuery(text);
    onTextChange?.(text);
    if (value !== null) {
      onChange(null);
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(text, cityKey).catch(() => undefined);
    }, DEBOUNCE_MS);
  };

  const handleSelect = (place: PlaceSuggestion) => {
    if (__DEV__) {
      console.log('[Tonyte][UploadLocation] selectedPlace', {
        venueName: place.venueName,
        address: place.address,
        city: place.city,
      });
      console.log('[Tonyte][UploadLocation] coordinates', { lat: place.lat, lng: place.lng });
    }
    setQuery(place.displayName);
    setSuggestions([]);
    setShowSuggestions(false);
    onChange(place);
  };

  const handleClear = () => {
    setQuery('');
    onTextChange?.('');
    setSuggestions([]);
    setShowSuggestions(false);
    onChange(null);
  };

  const isConfirmed = value !== null;

  return (
    <View style={styles.container}>
      <View style={[
        styles.inputRow,
        appearance.inputRow,
        isConfirmed && styles.inputRowConfirmed,
        isConfirmed && appearance.inputRowConfirmed,
      ]}>
        <Icon
          name="map-pin"
          size={16}
          color={isConfirmed ? theme.colors.accentSecondary : theme.colors.iconFaint}
          style={styles.inputIcon}
        />
        <TextInput
          style={[styles.input, appearance.input]}
          value={query}
          onChangeText={handleTextChange}
          placeholder={t('forms.searchAddressOrVenue')}
          placeholderTextColor={theme.colors.textMuted}
          returnKeyType="search"
          autoCorrect={false}
          autoCapitalize="none"
        />
        {loadingSuggestions ? (
          <ActivityIndicator size="small" color={theme.colors.accentSecondary} style={styles.spinner} />
        ) : isConfirmed ? (
          <TouchableOpacity onPress={handleClear} style={styles.clearBtn} activeOpacity={0.7}>
            <Icon name="x" size={15} color={theme.colors.iconSubtle} />
          </TouchableOpacity>
        ) : null}
      </View>

      {isConfirmed ? (
        <View style={styles.confirmedBadge}>
          <Icon name="check-circle" size={13} color={theme.colors.accentSecondary} />
          <Text style={[styles.confirmedText, appearance.confirmedText]} numberOfLines={1}>
            {value!.venueName ? `${value!.venueName} — ${value!.address}` : value!.address}
          </Text>
        </View>
      ) : null}

      {showSuggestions && suggestions.length > 0 ? (
        <View style={[styles.dropdown, appearance.dropdown]}>
          {suggestions.map((place, i) => (
            <TouchableOpacity
              key={place.placeId}
              style={[
                styles.suggestionItem,
                i > 0 && styles.suggestionSeparator,
                i > 0 && appearance.suggestionSeparator,
              ]}
              onPress={() => handleSelect(place)}
              activeOpacity={0.75}
            >
              <Icon name="map-pin" size={13} color={theme.colors.accent} style={styles.suggestionIcon} />
              <View style={styles.suggestionText}>
                <Text style={[styles.suggestionPrimary, appearance.suggestionPrimary]} numberOfLines={1}>
                  {place.venueName || place.address}
                </Text>
                {place.venueName ? (
                  <Text style={[styles.suggestionSecondary, appearance.suggestionSecondary]} numberOfLines={1}>
                    {place.address}
                  </Text>
                ) : null}
              </View>
            </TouchableOpacity>
          ))}
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { gap: 6 },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.065)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    borderRadius: Radius.lg,
    paddingHorizontal: Spacing.base,
    paddingVertical: Spacing.md,
    gap: 8,
  },
  inputRowConfirmed: {
    borderColor: `${Colors.turquoise}55`,
    backgroundColor: 'rgba(38,208,206,0.06)',
  },
  inputIcon: { flexShrink: 0 },
  input: {
    flex: 1,
    color: Colors.textPrimary,
    fontSize: Typography.base,
    padding: 0,
    margin: 0,
  },
  spinner: { flexShrink: 0 },
  clearBtn: { padding: 4, flexShrink: 0 },
  confirmedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 4,
  },
  confirmedText: {
    color: Colors.turquoise,
    fontSize: Typography.sm,
    flex: 1,
  },
  dropdown: {
    backgroundColor: '#0e1523',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.13)',
    borderRadius: Radius.lg,
    overflow: 'hidden',
    marginTop: 2,
  },
  suggestionItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 14,
    paddingVertical: 12,
    gap: 10,
  },
  suggestionSeparator: {
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.07)',
  },
  suggestionIcon: { marginTop: 2, flexShrink: 0 },
  suggestionText: { flex: 1 },
  suggestionPrimary: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
  },
  suggestionSecondary: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 12,
    marginTop: 2,
  },
});

const createThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  inputRow: daylightStyle(theme, {
    backgroundColor: theme.colors.inputBackground,
    borderColor: theme.colors.borderStrong,
    ...theme.shadows.card,
  }),
  inputRowConfirmed: daylightStyle(theme, {
    backgroundColor: theme.colors.accentSecondarySoft,
    borderColor: theme.colors.accentSecondaryBorder,
  }),
  input: daylightStyle(theme, { color: theme.colors.textPrimary }),
  confirmedText: daylightStyle(theme, { color: theme.colors.accentSecondary }),
  dropdown: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
    borderColor: theme.colors.borderStrong,
    ...theme.shadows.elevated,
  }),
  suggestionSeparator: daylightStyle(theme, { borderTopColor: theme.colors.divider }),
  suggestionPrimary: daylightStyle(theme, { color: theme.colors.textPrimary }),
  suggestionSecondary: daylightStyle(theme, { color: theme.colors.textSecondary }),
});

export default LocationAutocomplete;
